import { useEffect, useState } from "react";
import { getAllPaymentMethods } from "../services/api";
import type {
  IAllPaymentMethods,
  IBankAccountPaymentMethod,
  ICardPaymentMethod,
  ILastUsedFunding,
} from "../interfaces/payment-method";
import type { ICardState } from "../interfaces/card";
import { useStore } from "../store/globalState";
import { paymentMethodsCacheIsFresh } from "./payment-methods-cache";

/**
 * Module-level cache for the user's payment methods. It lets us warm the data
 * on the transfer page (mount) so the "select payment method" popup can render
 * its list instantly instead of fetching on open — which made the list "pop in"
 * and shift the layout once the request resolved.
 *
 * Keyed by the `cardList` identity captured at fetch time. Adding a card
 * replaces that array while the quote page is unmounted, so a remount must
 * refetch. A ref seeded with the current list cannot see that change.
 */
let cache: {
  linkId: string;
  data: IAllPaymentMethods;
  cardList: readonly ICardState[];
} | null = null;

let inflight: {
  linkId: string;
  cardList: readonly ICardState[];
  promise: Promise<IAllPaymentMethods>;
  seq: number;
} | null = null;

let requestSeq = 0;

const fetchPaymentMethods = (
  linkId: string,
  cardList: readonly ICardState[],
): Promise<IAllPaymentMethods> => {
  if (paymentMethodsCacheIsFresh(cache, linkId, cardList)) {
    return Promise.resolve(cache!.data);
  }
  if (
    inflight &&
    inflight.linkId === linkId &&
    inflight.cardList === cardList
  ) {
    return inflight.promise;
  }

  const seq = ++requestSeq;
  const promise = getAllPaymentMethods(linkId)
    .then((data) => {
      if (seq === requestSeq) {
        cache = { linkId, data, cardList };
      }
      if (inflight?.seq === seq) inflight = null;
      return data;
    })
    .catch((err) => {
      if (inflight?.seq === seq) inflight = null;
      throw err;
    });

  inflight = { linkId, cardList, promise, seq };
  return promise;
};

export interface UsePaymentMethodsResult {
  /** Enabled cards only. */
  cards: ICardPaymentMethod[];
  bankAccounts: IBankAccountPaymentMethod[];
  lastUsedFunding: ILastUsedFunding | null;
  /** True only on a cold load (no cached data to show yet). */
  loading: boolean;
}

/**
 * Shared, cached access to the user's payment methods. Both the transfer page
 * and the payment-method popup consume this so the network request happens once
 * and the popup opens with the list already populated.
 *
 * Re-fetches whenever `cardList` changes — including when the quote page
 * remounts after the add-card flow registers a new instrument.
 */
export const usePaymentMethods = (
  linkId: string | null | undefined,
): UsePaymentMethodsResult => {
  const { cardList } = useStore();

  const sameLinkCache = !!(linkId && cache && cache.linkId === linkId);
  const [data, setData] = useState<IAllPaymentMethods | null>(
    sameLinkCache ? cache!.data : null,
  );
  const [loading, setLoading] = useState(!sameLinkCache);

  useEffect(() => {
    if (!linkId) return;

    if (paymentMethodsCacheIsFresh(cache, linkId, cardList) && cache) {
      setData(cache.data);
      setLoading(false);
      return;
    }

    if (cache?.linkId === linkId) {
      setData(cache.data);
      setLoading(false);
    } else {
      setData(null);
      setLoading(true);
    }

    let active = true;
    fetchPaymentMethods(linkId, cardList)
      .then((res) => {
        if (!active) return;
        setData(res);
        setLoading(false);
      })
      .catch(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [linkId, cardList]);

  return {
    cards: (data?.cards ?? []).filter((c) => c.enabled),
    bankAccounts: (data?.bankAccounts ?? []).filter((a) => a.enabled),
    lastUsedFunding: data?.lastUsedFunding ?? null,
    loading,
  };
};
